/**
 * Color utility functions for the resource planning scheduler.
 *
 * Provides:
 * - monday.com default palette colours
 * - Workload heat-map color calculations
 * - Contrast / accessibility helpers
 */

// ─── monday.com colour palette ───────────────────────────────────────────────

/** Default monday.com group colors. */
export const MONDAY_GROUP_COLORS = [
  '#579bfc', // Blue (default)
  '#a25ddc', // Purple
  '#e2445c', // Red
  '#fdab3d', // Orange
  '#00c875', // Green
  '#0086c0', // Dark Blue
  '#bb3354', // Dark Red
  '#ff158a', // Pink
  '#ff5ac4', // Hot Pink
  '#cab641', // Yellow
  '#9cd326', // Lime
  '#7e3b8a', // Dark Purple
  '#037f4c', // Dark Green
  '#66ccff', // Light Blue
] as const;

// ─── Workload colours ────────────────────────────────────────────────────────

export const WORKLOAD_COLORS = {
  /** Underloaded (0-50% utilisation) — room for more work */
  underloaded: '#66ccff',
  /** At or near capacity (51-100% utilisation) */
  green: '#00c875',
  /** Near overload (101-120% utilisation) */
  yellow: '#fdab3d',
  /** Overloaded (>120% utilisation) */
  red: '#e2445c',
  /** No data / zero hours */
  empty: '#f5f6f8',
  /** Vacation-only day (user on leave, no items scheduled) */
  vacation: '#579bfc',
  /** Conflict: items scheduled on a leave day */
  conflict: '#a25ddc',
  /** Holiday day (public holiday from calendar) */
  holiday: '#ff9800',
} as const;

/**
 * Determine the workload color based on utilisation percentage.
 *
 * @param hoursScheduled    - Hours scheduled for the period
 * @param hoursCapacity     - Available capacity hours for the period
 * @param isLeave           - Whether the user is on leave this day/period
 * @param leaveDayType      - Leave type: 'full', 'am', 'pm', or null
 * @param hasLeaveConflict  - Items exist on a full-day leave (hours were redistributed)
 * @param isHoliday         - Whether this is a holiday day for the user
 * @param isPastDay         - Whether this is a past day (used by remaining_estimate mode)
 * @param indicatorType     - Active indicator type (for mode-specific coloring)
 * @param worklogHours      - Hours logged on this date (for worklog_workload mode)
 * @returns Hex color string
 */
export function getWorkloadColor(
  hoursScheduled: number,
  hoursCapacity: number,
  isLeave?: boolean,
  leaveDayType?: 'full' | 'am' | 'pm' | null,
  hasLeaveConflict?: boolean,
  isHoliday?: boolean,
  isPastDay?: boolean,
  indicatorType?: string,
  worklogHours?: number,
): string {
  // Remaining estimate and availability modes: past days are always gray (show "–")
  if (isPastDay && (indicatorType === 'remaining_estimate' || indicatorType === 'availability')) {
    if (isHoliday) return WORKLOAD_COLORS.holiday;
    if (isLeave) return WORKLOAD_COLORS.vacation;
    return WORKLOAD_COLORS.empty;
  }

  // Worklog + Workload mode: past days with worklogs on non-working days → purple
  if (isPastDay && indicatorType === 'worklog_workload' && (worklogHours ?? 0) > 0 && hoursCapacity <= 0) {
    return WORKLOAD_COLORS.conflict;
  }

  // Holiday: orange — whether items are scheduled or not
  if (isHoliday) {
    return WORKLOAD_COLORS.holiday;
  }

  // Conflict: full-day leave but items are still scheduled (or were redistributed)
  if (isLeave && leaveDayType === 'full' && (hoursScheduled > 0 || hasLeaveConflict)) {
    return WORKLOAD_COLORS.conflict;
  }

  // Vacation-only day: leave with no items scheduled
  if (isLeave && hoursScheduled === 0) return WORKLOAD_COLORS.vacation;

  // Standard utilisation logic (also handles half-day leave via reduced hoursCapacity)
  if (hoursCapacity <= 0 || hoursScheduled === 0) return WORKLOAD_COLORS.empty;

  const utilisation = hoursScheduled / hoursCapacity;

  if (utilisation > 1.2) return WORKLOAD_COLORS.red;
  if (utilisation > 1.0) return WORKLOAD_COLORS.yellow;
  if (utilisation > 0.5) return WORKLOAD_COLORS.green;
  return WORKLOAD_COLORS.underloaded;
}

// ─── Colour manipulation helpers ─────────────────────────────────────────────

/**
 * Convert a hex colour to rgba with the given opacity.
 */
export function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Determine whether white or black text provides better contrast against
 * a given background hex colour.
 *
 * Uses W3C relative luminance formula.
 */
export function getContrastTextColor(bgHex: string): '#ffffff' | '#323338' {
  const r = parseInt(bgHex.slice(1, 3), 16) / 255;
  const g = parseInt(bgHex.slice(3, 5), 16) / 255;
  const b = parseInt(bgHex.slice(5, 7), 16) / 255;

  // sRGB to linear
  const rLin = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  const gLin = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  const bLin = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

  const luminance = 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin;

  // monday.com uses #323338 as their primary text colour instead of pure black
  return luminance > 0.179 ? '#323338' : '#ffffff';
}

/**
 * Darken a hex colour by a given percentage (0-100).
 */
export function darken(hex: string, percent: number): string {
  const factor = 1 - percent / 100;
  const r = Math.round(parseInt(hex.slice(1, 3), 16) * factor);
  const g = Math.round(parseInt(hex.slice(3, 5), 16) * factor);
  const b = Math.round(parseInt(hex.slice(5, 7), 16) * factor);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Lighten a hex colour by a given percentage (0-100).
 */
export function lighten(hex: string, percent: number): string {
  const factor = percent / 100;
  const r = Math.round(
    parseInt(hex.slice(1, 3), 16) + (255 - parseInt(hex.slice(1, 3), 16)) * factor,
  );
  const g = Math.round(
    parseInt(hex.slice(3, 5), 16) + (255 - parseInt(hex.slice(3, 5), 16)) * factor,
  );
  const b = Math.round(
    parseInt(hex.slice(5, 7), 16) + (255 - parseInt(hex.slice(5, 7), 16)) * factor,
  );
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
